import { Button } from "@prisma/ui";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, Copy, FileUp, RotateCcw } from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";

import { Dialog } from "../../components/Dialog";
import { ApiError } from "../../lib/api";
import { formatBytes } from "../../lib/format";
import { uploadFile, type UploadResult, type UploadTarget } from "../../lib/upload";

type Item = { file: File; progress: number; state: "queued" | "uploading" | "done" | "failed"; error?: string; result?: UploadResult };
const SHOOT_TYPES = ["RAW", "BROLL", "TESTIMONIAL", "INTERVIEW", "REFERENCE", "PRODUCT_ASSET"] as const;
const ATTACHMENT_KINDS = ["BRIEF", "STORYBOARD", "SCRIPT", "LICENCE", "RELEASE_FORM", "OTHER"] as const;
const ACCEPT_MEDIA = "video/mp4,video/quicktime,video/x-matroska,video/webm,image/jpeg,image/png,image/webp,image/heic,image/heif,audio/mpeg,audio/wav,audio/mp4";
const ACCEPT_FILES = `${ACCEPT_MEDIA},application/pdf`;

/** Shoot Asset Upload (doc 04 P17) and Related Files upload. The user sees files, sizes and
 *  progress; Prisma decides where the bytes live (no NAS path is ever shown). */
export function UploadDrawer({ open, onClose, target, meta, invalidate, fixedKind, titleKey, onUploaded }: {
  open: boolean;
  onClose: () => void;
  target: Omit<UploadTarget, "asset_type" | "attachment_kind">;
  meta: { label: string; value: string }[];
  invalidate: string[][];
  fixedKind?: boolean; // editing cuts: the server decides (FINAL); no type choice
  titleKey?: string;
  onUploaded?: (r: UploadResult) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [kind, setKind] = useState<string>(target.target_kind === "attachment" ? "OTHER" : "RAW");
  const [busy, setBusy] = useState(false);
  const isAttachment = target.target_kind === "attachment";

  const add = (files: FileList | null) => {
    if (!files) return;
    // Copy now: a FileList is live and empties when the input is reset.
    const picked = [...files].map((file) => ({ file, progress: 0, state: "queued" as const }));
    setItems((cur) => [...cur, ...picked]);
  };
  const run = async (only?: number) => {
    setBusy(true);
    const list = [...items];
    for (let i = 0; i < list.length; i++) {
      const it = list[i];
      if (!it || (only !== undefined && i !== only) || it.state === "done") continue;
      const update = (patch: Partial<Item>) => setItems((cur) => cur.map((x, n) => (n === i ? { ...x, ...patch } : x)));
      update({ state: "uploading", error: undefined });
      try {
        const extra = fixedKind ? {} : isAttachment ? { attachment_kind: kind } : { asset_type: kind };
        const result = await uploadFile(it.file, { ...target, ...extra }, (p) => update({ progress: p }));
        update({ state: "done", progress: 1, result });
        onUploaded?.(result);
      } catch (e) {
        const code = e instanceof ApiError ? e.code : "unknown";
        update({ state: "failed", error: code });
      }
    }
    for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
    setBusy(false);
  };
  const drop = (e: DragEvent) => { e.preventDefault(); add(e.dataTransfer.files); };
  const total = items.reduce((n, x) => n + x.file.size, 0);
  const close = () => { if (!busy) { setItems([]); onClose(); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()} title={t(titleKey ?? (isAttachment ? "assets.upload_files_title" : "assets.upload_title"))} description={t("assets.upload_body")} wide>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <div onDragOver={(e) => e.preventDefault()} onDrop={drop}
            className="flex flex-col items-center justify-center gap-2 rounded-[var(--radius-card)] border-2 border-dashed border-line bg-elevated p-8 text-center">
            <FileUp aria-hidden="true" className="size-8 text-violet" />
            <p className="text-[14px] text-ink">{t(isAttachment ? "assets.drop_files" : "assets.drop_media")}</p>
            <Button variant="secondary" onClick={() => input.current?.click()}>{t("assets.choose_files")}</Button>
            <input ref={input} type="file" multiple hidden accept={isAttachment ? ACCEPT_FILES : ACCEPT_MEDIA} aria-label={t("assets.choose_files")}
              onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          </div>
          {!fixedKind && (
            <>
              <label htmlFor="upload-kind" className="mb-1 mt-4 block text-[13px] font-medium text-ink-2">{t(isAttachment ? "assets.file_kind" : "assets.asset_type")}</label>
              <select id="upload-kind" value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy}
                className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
                {(isAttachment ? ATTACHMENT_KINDS : SHOOT_TYPES).map((k) => (
                  <option key={k} value={k}>{t(isAttachment ? `assets.attachment_${k.toLowerCase()}` : `assets.type_${k.toLowerCase()}`)}</option>
                ))}
              </select>
            </>
          )}
          {items.length > 0 && (
            <>
              <p className="mt-4 text-[13px] text-ink-2">{t("assets.summary", { count: items.length, size: formatBytes(total) })}</p>
              <ul className="mt-2 space-y-2" aria-label={t("assets.files")}>
                {items.map((it, i) => (
                  <li key={`${it.file.name}-${i}`} className="rounded-[var(--radius-control)] border border-line p-3">
                    <div className="flex items-center justify-between gap-3 text-[13px]">
                      <span className="min-w-0 truncate text-ink">{it.file.name}</span>
                      <span className="shrink-0 text-ink-2">{formatBytes(it.file.size)}</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-soft" role="progressbar" aria-label={it.file.name}
                      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(it.progress * 100)}>
                      <div className="h-full bg-[image:var(--gradient-primary-button)]" style={{ width: `${Math.round(it.progress * 100)}%` }} />
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[12px]">
                      {it.state === "done" && (it.result?.deduplicated
                        ? <span className="flex items-center gap-1 text-[var(--status-info)]"><Copy aria-hidden="true" className="size-3.5" />{t("assets.deduplicated", { id: it.result.display_id ?? "" })}</span>
                        : <span className="flex items-center gap-1 text-[var(--status-success)]"><CheckCircle2 aria-hidden="true" className="size-3.5" />{it.result?.version ? t("editor.uploaded_version", { n: it.result.version }) : (it.result?.display_id ?? t("assets.uploaded"))}</span>)}
                      {it.state === "failed" && (
                        <>
                          <span className="flex items-center gap-1 text-[var(--status-danger)]"><CircleAlert aria-hidden="true" className="size-3.5" />{t(`error.${it.error}`, { defaultValue: t("error.unknown") })}</span>
                          <button type="button" className="flex items-center gap-1 text-violet-text underline" onClick={() => void run(i)} disabled={busy}>
                            <RotateCcw aria-hidden="true" className="size-3.5" />{t("assets.resume")}
                          </button>
                        </>
                      )}
                      {it.state === "uploading" && <span className="text-ink-2">{t("assets.uploading")}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <aside className="rounded-[var(--radius-card)] bg-elevated p-4 text-[13px]">
          <h3 className="font-semibold text-ink">{t("assets.auto_metadata")}</h3>
          <dl className="mt-2 space-y-2">
            {meta.map((m) => (
              <div key={m.label}><dt className="text-ink-2">{m.label}</dt><dd className="text-ink">{m.value}</dd></div>
            ))}
          </dl>
          <p className="mt-3 text-[12px] text-ink-2">{t("assets.storage_note")}</p>
        </aside>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={close} disabled={busy}>{t("common.close")}</Button>
        <Button variant="primary" disabled={busy || !items.some((x) => x.state === "queued")} onClick={() => void run()}>{t("assets.start_upload")}</Button>
      </div>
    </Dialog>
  );
}
